<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import ConfirmContactDeleteDialog from 'dashboard/components-next/Contacts/ContactsForm/ConfirmContactDeleteDialog.vue';
import Policy from 'dashboard/components/policy.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['goToContactsList']);

const { t, locale } = useI18n();

const confirmDeleteContactDialogRef = ref(null);

const createdAt = computed(() => {
  return props.selectedContact?.createdAt
    ? dynamicTime(props.selectedContact.createdAt, locale.value)
    : '';
});

const erpLinkStatus = computed(
  () => props.selectedContact?.additionalAttributes?.erpLinkStatus || 'unlinked'
);

const erpStatusLabel = computed(() => {
  const map = {
    linked: t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERP_STATUS.LINKED'),
    sync_required: t(
      'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERP_STATUS.SYNC_REQUIRED'
    ),
    sync_failed: t(
      'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERP_STATUS.SYNC_FAILED'
    ),
  };
  return (
    map[erpLinkStatus.value] ||
    t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERP_STATUS.UNLINKED')
  );
});

const erpStatusClass = computed(() => {
  const map = {
    linked: 'bg-n-teal-3 text-n-teal-11',
    sync_required: 'bg-n-amber-3 text-n-amber-11',
    sync_failed: 'bg-n-ruby-3 text-n-ruby-11',
  };
  return map[erpLinkStatus.value] || 'bg-n-alpha-2 text-n-slate-10';
});

const ERP_BASE_URL = 'https://erp.eltafouk.com';

const erpUrl = computed(() => {
  const code = props.selectedContact?.additionalAttributes?.erpCustomer;
  return code ? `${ERP_BASE_URL}/app/customer/${encodeURIComponent(code)}` : '';
});

const lastActivityAt = computed(() => {
  return props.selectedContact?.lastActivityAt
    ? dynamicTime(props.selectedContact.lastActivityAt, locale.value)
    : '';
});

const avatarSrc = computed(() => props.selectedContact?.thumbnail);

const openConfirmDeleteContactDialog = () => {
  confirmDeleteContactDialogRef.value?.dialogRef.open();
};
</script>

<template>
  <div class="flex flex-col items-start gap-8 pb-6">
    <div class="flex flex-col items-start gap-3">
      <Avatar
        :src="avatarSrc || ''"
        :name="selectedContact?.name || ''"
        :size="72"
      />
      <div class="flex flex-col gap-1">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ selectedContact?.name }}
        </h3>
        <div class="flex flex-col gap-1.5">
          <div class="flex items-center gap-2">
            <span
              v-if="selectedContact?.identifier"
              class="inline-flex items-center gap-1 text-sm text-n-slate-11"
            >
              <span class="i-ph-user-gear text-n-slate-10 size-4" />
              {{ selectedContact?.identifier }}
            </span>
            <span
              class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
              :class="erpStatusClass"
            >
              {{ erpStatusLabel }}
            </span>
            <a
              v-if="erpUrl"
              :href="erpUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center gap-1 text-xs font-medium text-n-blue-11 hover:underline"
            >
              <span class="i-ph-arrow-square-out size-3.5" />
              {{
                $t(
                  'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERP_STATUS.OPEN_IN_ERP'
                )
              }}
            </a>
          </div>
          <span
            dir="auto"
            class="inline-flex items-center gap-1 text-sm text-n-slate-11"
          >
            <span class="i-ph-activity text-n-slate-10 size-4" />
            {{ $t('CONTACTS_LAYOUT.DETAILS.CREATED_AT', { date: createdAt }) }}
            •
            {{
              $t('CONTACTS_LAYOUT.DETAILS.LAST_ACTIVITY', {
                date: lastActivityAt,
              })
            }}
          </span>
        </div>
      </div>
      <ContactLabels :contact-id="selectedContact?.id" />
    </div>
    <fieldset
      disabled
      class="flex flex-col items-start w-full gap-6 p-0 m-0 border-0 min-w-0"
    >
      <ContactsForm :contact-data="selectedContact" is-details-view />
    </fieldset>
    <Policy :permissions="['administrator']">
      <div
        class="flex flex-col items-start w-full gap-4 pt-6 border-t border-n-strong"
      >
        <div class="flex flex-col gap-2">
          <h6 class="text-base font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT') }}
          </h6>
          <span class="text-sm text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT_DESCRIPTION') }}
          </span>
        </div>
        <Button
          :label="t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT')"
          color="ruby"
          @click="openConfirmDeleteContactDialog"
        />
      </div>
      <ConfirmContactDeleteDialog
        ref="confirmDeleteContactDialogRef"
        :selected-contact="selectedContact"
        @go-to-contacts-list="emit('goToContactsList')"
      />
    </Policy>
  </div>
</template>
